import { useState } from "react";
import { API_URL } from "src/util/config";
import { checkResponse } from "src/util/helpers";
import { PoClosureRequest, PoClosureRequestStatus } from "models/po-closure";
import { useAuth } from "src/contexts/AuthContext";

export interface PoClosureAuditFilters {
  yearMonth: string;
  status?: PoClosureRequestStatus | "";
  requesterEmail?: string;
  lookup?: string;
  limit?: number;
  cursor?: string;
}

export const usePoClosureRequests = () => {
  const [items, setItems] = useState<PoClosureRequest[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithReauth } = useAuth();

  const fetchPoClosureRequests = async (filters: PoClosureAuditFilters) => {
    setLoading(true);
    setError(null);
    try {
      const query = new URLSearchParams();
      query.append("yearMonth", filters.yearMonth);
      query.append("limit", String(filters.limit || 50));
      if (filters.status) query.append("status", filters.status);
      if (filters.requesterEmail?.trim()) {
        query.append("requesterEmail", filters.requesterEmail.trim());
      }
      if (filters.lookup?.trim()) {
        query.append("lookup", filters.lookup.trim());
      }
      if (filters.cursor?.trim()) {
        query.append("cursor", filters.cursor.trim());
      }

      const response = await fetchWithReauth(
        `${API_URL}/po-closures?${query.toString()}`,
        { method: "GET" },
      );
      const data = (await checkResponse(response)) as {
        items?: PoClosureRequest[];
        nextCursor?: string;
      };
      setItems((data.items || []) as PoClosureRequest[]);
      setNextCursor(data.nextCursor || undefined);
    } catch (requestsError) {
      console.error("Error fetching PO closure requests:", requestsError);
      setError("Failed to load PO closure history.");
      setItems([]);
      setNextCursor(undefined);
    } finally {
      setLoading(false);
    }
  };

  return { items, nextCursor, loading, error, fetchPoClosureRequests };
};
